import {
  ChangeJobStatusResponse,
  ChangeWatchlistResponse,
  ChangeDownloadResponse,
  CreateMediaLinkRequest,
  CreateMediaLinkResponse,
  DeleteDownloadResponse,
  ListDownloadsRequest,
  ListDownloadsResponse,
  ListRenditionsRequest,
  ListRenditionsResponse,
  RefreshMovieRequest,
  RefreshMovieResponse,
  StartDownloadRequest,
  StartDownloadResponse,
  CreateBookmarkRequest,
  CreateBookmarkResponse,
  CreateListResponse,
  DeleteBookmarkResponse,
  DeleteListResponse,
  GetListResponse,
  ListBookmarksRequest,
  ListBookmarksResponse,
  ListListsResponse,
  PutListMovieRequest,
  PutListMovieResponse,
  RemoveListMovieResponse,
  RenameListResponse,
  UpdateBookmarkRequest,
  UpdateBookmarkResponse,
  DeleteRatePolicyResponse,
  GetHealthResponse,
  GetCategoryPagesResponse,
  GetCrawlItemResponse,
  GetJobHistoryResponse,
  GetJobResponse,
  GetMovieResponse,
  GetPersonResponse,
  GetTermResponse,
  ListContinueWatchingRequest,
  ListContinueWatchingResponse,
  ListCategoriesRequest,
  ListCategoriesResponse,
  ListDeadItemsRequest,
  ListJobItemsRequest,
  ListJobItemsResponse,
  ListJobRunsRequest,
  ListJobRunsResponse,
  ListSourcesResponse,
  PreviewRefreshRequest,
  PreviewRefreshResponse,
  ListDeadItemsResponse,
  ListHistoryResponse,
  ListJobsRequest,
  ListJobsResponse,
  ListMoviesRequest,
  ListPeopleRequest,
  ListPeopleResponse,
  ListMoviesResponse,
  ListRatePoliciesResponse,
  ListTermsRequest,
  ListTermsResponse,
  PingResponse,
  PutRatePolicyRequest,
  PutRatePolicyResponse,
  RecordProgressRequest,
  RecordProgressResponse,
  RetryDeadItemsResponse,
  StartJobRequest,
  StartJobResponse,
  StartSessionResponse,
  UpdateMarkRequest,
  UpdateMarkResponse,
  UpdatePersonMarkRequest,
  UpdatePersonMarkResponse,
} from '@zyx/shared';
import { HttpClient, HttpClientProps } from './http-client';

/** One method per endpoint, compiled against the same contracts the service implements. */
export class ZyxClient {
  private readonly http: HttpClient;
  private readonly credentials?: HttpClientProps['credentials'];

  constructor(props: HttpClientProps) {
    this.http = new HttpClient(props);
    this.credentials = props.credentials;
  }

  /** Where the service is, as the app was told. */
  get baseUrl(): string {
    return this.http.base;
  }

  /** The URL of a cached image, clip or thumbnail, by its hash. */
  assetUrl(sha256: string): string {
    return `${this.http.base}/assets/${sha256}`;
  }

  /** An episode's playlist, through the service's relay; what the player loads. */
  playUrl(episodeId: number): string {
    return `${this.http.base}/play/${episodeId}/master.m3u8`;
  }

  /** A file of an offline copy: `index.m3u8` for the player, or a segment it names. */
  downloadFileUrl(downloadId: number, name: string): string {
    return `${this.http.base}/downloads/${downloadId}/files/${encodeURIComponent(name)}`;
  }

  /** An offline copy as one MP4, for saving to a device. `parts` names the part in the file name. */
  downloadMp4Url(downloadId: number, parts: boolean): string {
    return `${this.http.base}/downloads/${downloadId}/movie.mp4${parts ? '?parts=1' : ''}`;
  }

  /** A signed link that plays without the token or cookie, as an absolute URL: what AirPlay hands an Apple TV. */
  async mediaLink(request: CreateMediaLinkRequest): Promise<string> {
    const { path } = await this.http.request<CreateMediaLinkResponse>('POST', '/media-links', { body: request });
    return `${this.http.base}${path}`;
  }

  listRenditions(request: ListRenditionsRequest): Promise<ListRenditionsResponse> {
    return this.http.request('GET', `/movies/${encodeURIComponent(request.code)}/renditions`, { query: { variant: request.variantId } });
  }

  startDownload(request: StartDownloadRequest): Promise<StartDownloadResponse> {
    return this.http.request('POST', `/movies/${encodeURIComponent(request.code)}/downloads`, { body: { variantId: request.variantId, resolutions: request.resolutions } });
  }

  listDownloads(request: ListDownloadsRequest = {}): Promise<ListDownloadsResponse> {
    return this.http.request('GET', '/downloads', { query: { code: request.code } });
  }

  cancelDownload(downloadId: number): Promise<ChangeDownloadResponse> {
    return this.http.request('POST', `/downloads/${downloadId}/cancel`);
  }

  retryDownload(downloadId: number): Promise<ChangeDownloadResponse> {
    return this.http.request('POST', `/downloads/${downloadId}/retry`);
  }

  deleteDownload(downloadId: number): Promise<DeleteDownloadResponse> {
    return this.http.request('DELETE', `/downloads/${downloadId}`);
  }

  refreshMovie(request: RefreshMovieRequest): Promise<RefreshMovieResponse> {
    const { code, ...body } = request;
    return this.http.request('POST', `/movies/${encodeURIComponent(code)}/refresh`, { body });
  }

  listHistory(limit?: number): Promise<ListHistoryResponse> {
    return this.http.request('GET', '/history', { query: { limit } });
  }

  /** A cached text file, such as a thumbnail WebVTT. */
  async getAssetText(sha256: string): Promise<string> {
    const response = await fetch(this.assetUrl(sha256), { credentials: this.credentials });
    if (!response.ok) {
      throw new Error(`Cached file ${sha256} answered HTTP ${response.status}.`);
    }
    return response.text();
  }

  ping(): Promise<PingResponse> {
    return this.http.request('GET', '/ping');
  }

  getHealth(): Promise<GetHealthResponse> {
    return this.http.request('GET', '/health');
  }

  startSession(): Promise<StartSessionResponse> {
    return this.http.request('POST', '/session');
  }

  listMovies(request: ListMoviesRequest = {}): Promise<ListMoviesResponse> {
    return this.http.request('GET', '/movies', {
      query: {
        q: request.q,
        person: request.personId,
        term: request.termId,
        edition: request.edition,
        favourite: request.favourite,
        watchlist: request.watchlist,
        list: request.listId,
        hidden: request.hidden,
        sort: request.sort,
        page: request.page,
        limit: request.limit,
      },
    });
  }

  getMovie(code: string): Promise<GetMovieResponse> {
    return this.http.request('GET', `/movies/${encodeURIComponent(code)}`);
  }

  listContinueWatching(request: ListContinueWatchingRequest = {}): Promise<ListContinueWatchingResponse> {
    return this.http.request('GET', '/continue-watching', { query: { limit: request.limit } });
  }

  listPeople(request: ListPeopleRequest = {}): Promise<ListPeopleResponse> {
    return this.http.request('GET', '/people', { query: { q: request.q, favourite: request.favourite, limit: request.limit } });
  }

  updatePersonMark(request: UpdatePersonMarkRequest): Promise<UpdatePersonMarkResponse> {
    return this.http.request('PUT', `/people/${request.personId}/mark`, { body: { favourite: request.favourite } });
  }

  getPerson(personId: number): Promise<GetPersonResponse> {
    return this.http.request('GET', `/people/${personId}`);
  }

  listTerms(request: ListTermsRequest): Promise<ListTermsResponse> {
    return this.http.request('GET', '/terms', { query: { kind: request.kind, q: request.q, limit: request.limit } });
  }

  getTerm(termId: number): Promise<GetTermResponse> {
    return this.http.request('GET', `/terms/${termId}`);
  }

  updateMark(request: UpdateMarkRequest): Promise<UpdateMarkResponse> {
    const { code, ...body } = request;
    return this.http.request('PUT', `/movies/${encodeURIComponent(code)}/mark`, { body });
  }

  addToWatchlist(code: string): Promise<ChangeWatchlistResponse> {
    return this.http.request('PUT', `/movies/${encodeURIComponent(code)}/watchlist`);
  }

  removeFromWatchlist(code: string): Promise<ChangeWatchlistResponse> {
    return this.http.request('DELETE', `/movies/${encodeURIComponent(code)}/watchlist`);
  }

  recordProgress(request: RecordProgressRequest): Promise<RecordProgressResponse> {
    return this.http.request('POST', '/progress', { body: request });
  }

  listLists(): Promise<ListListsResponse> {
    return this.http.request('GET', '/lists');
  }

  getList(listId: number): Promise<GetListResponse> {
    return this.http.request('GET', `/lists/${listId}`);
  }

  createList(name: string): Promise<CreateListResponse> {
    return this.http.request('POST', '/lists', { body: { name } });
  }

  renameList(listId: number, name: string): Promise<RenameListResponse> {
    return this.http.request('PUT', `/lists/${listId}`, { body: { name } });
  }

  deleteList(listId: number): Promise<DeleteListResponse> {
    return this.http.request('DELETE', `/lists/${listId}`);
  }

  /** Adds a movie to a list, or moves it: see {@link PutListMovieRequest.after}. */
  putListMovie(request: PutListMovieRequest): Promise<PutListMovieResponse> {
    return this.http.request('PUT', `/lists/${request.listId}/movies/${encodeURIComponent(request.code)}`, { body: request.after === undefined ? {} : { after: request.after } });
  }

  removeListMovie(listId: number, code: string): Promise<RemoveListMovieResponse> {
    return this.http.request('DELETE', `/lists/${listId}/movies/${encodeURIComponent(code)}`);
  }

  listBookmarks(request: ListBookmarksRequest = {}): Promise<ListBookmarksResponse> {
    return this.http.request('GET', '/bookmarks', { query: { code: request.code, limit: request.limit } });
  }

  createBookmark(request: CreateBookmarkRequest): Promise<CreateBookmarkResponse> {
    return this.http.request('POST', '/bookmarks', { body: request });
  }

  updateBookmark(request: UpdateBookmarkRequest): Promise<UpdateBookmarkResponse> {
    return this.http.request('PUT', `/bookmarks/${request.bookmarkId}`, { body: { label: request.label } });
  }

  deleteBookmark(bookmarkId: number): Promise<DeleteBookmarkResponse> {
    return this.http.request('DELETE', `/bookmarks/${bookmarkId}`);
  }

  startJob(request: StartJobRequest): Promise<StartJobResponse> {
    return this.http.request('POST', '/jobs', { body: request });
  }

  previewRefresh(request: PreviewRefreshRequest): Promise<PreviewRefreshResponse> {
    return this.http.request('GET', '/refresh-preview', {
      query: { source: request.sourceId, pagesOlderThanDays: request.pagesOlderThanDays, streamsOlderThanDays: request.streamsOlderThanDays },
    });
  }

  listJobs(request: ListJobsRequest = {}): Promise<ListJobsResponse> {
    return this.http.request('GET', '/jobs', { query: { limit: request.limit } });
  }

  getJob(jobId: number): Promise<GetJobResponse> {
    return this.http.request('GET', `/jobs/${jobId}`);
  }

  listSources(): Promise<ListSourcesResponse> {
    return this.http.request('GET', '/sources');
  }

  listCategories(request: ListCategoriesRequest): Promise<ListCategoriesResponse> {
    return this.http.request('GET', `/sources/${encodeURIComponent(request.sourceId)}/categories`, {
      query: { kind: request.kind, q: request.query === '' ? undefined : request.query, limit: request.limit, offset: request.offset },
    });
  }

  getCategoryPages(sourceId: string, path: string): Promise<GetCategoryPagesResponse> {
    return this.http.request('GET', `/sources/${encodeURIComponent(sourceId)}/category-pages`, { query: { path } });
  }

  getJobHistory(jobId: number): Promise<GetJobHistoryResponse> {
    return this.http.request('GET', `/jobs/${jobId}/history`);
  }

  getCrawlItem(itemId: number): Promise<GetCrawlItemResponse> {
    return this.http.request('GET', `/crawl-items/${itemId}`);
  }

  listJobItems(request: ListJobItemsRequest): Promise<ListJobItemsResponse> {
    return this.http.request('GET', `/jobs/${request.jobId}/items`, {
      query: { status: request.status, kind: request.kind, limit: request.limit, offset: request.offset },
    });
  }

  listJobRuns(request: ListJobRunsRequest): Promise<ListJobRunsResponse> {
    return this.http.request('GET', `/jobs/${request.jobId}/runs`, { query: { limit: request.limit } });
  }

  listDeadItems(request: ListDeadItemsRequest): Promise<ListDeadItemsResponse> {
    return this.http.request('GET', `/jobs/${request.jobId}/dead`, { query: { limit: request.limit } });
  }

  pauseJob(jobId: number): Promise<ChangeJobStatusResponse> {
    return this.http.request('POST', `/jobs/${jobId}/pause`);
  }

  resumeJob(jobId: number): Promise<ChangeJobStatusResponse> {
    return this.http.request('POST', `/jobs/${jobId}/resume`);
  }

  cancelJob(jobId: number): Promise<ChangeJobStatusResponse> {
    return this.http.request('POST', `/jobs/${jobId}/cancel`);
  }

  retryDeadItems(jobId: number): Promise<RetryDeadItemsResponse> {
    return this.http.request('POST', `/jobs/${jobId}/retry-dead`);
  }

  listRatePolicies(): Promise<ListRatePoliciesResponse> {
    return this.http.request('GET', '/rate-policies');
  }

  putRatePolicy(request: PutRatePolicyRequest): Promise<PutRatePolicyResponse> {
    const { policyId, ...body } = request;
    return this.http.request('PUT', `/rate-policies/${encodeURIComponent(policyId)}`, { body });
  }

  deleteRatePolicy(policyId: string): Promise<DeleteRatePolicyResponse> {
    return this.http.request('DELETE', `/rate-policies/${encodeURIComponent(policyId)}`);
  }
}
